import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography, Tooltip, IconButton, Paper, Grid, CircularProgress, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import ShiftFilterDisplay from './simpe-auto-assign/ShiftFilterDisplay';
import AllocationPreviewDisplay from './simpe-auto-assign/AllocationPreviewDisplay';
import AgentPreviewDisplay from './simpe-auto-assign/AgentPreviewDisplay'; // Import the new agent preview component
import { removeNullValueFromObject } from '../../../../../../utility/object-related';

import axios from 'axios';
import { CONFIG } from '../../../../../../config';

const API_HOST_CRM = CONFIG.modules['crm'].host;

const remoteNullValueFromObject = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null)
  );
};

/**
 * This is the simple auto assign algorithm component.
 * It is used to assign the filtered out cases in the main table using the shift selected.
 * 
 * @param {Object} props - The component props.
 * @param {Array} props.usersInShift - The users in the shift.
 * @param {Object} props.shift - The shift object.
 * @param {Object} props.filter - The filter object.
 * @param {boolean} props.autoAssigning - Whether the auto assigning is in progress.
 * @param {function} props.setAutoAssigning - The function to set the auto assigning state.
 * @param {function} [props.onAssignmentSuccess] - Optional callback function executed after successful assignment.
 * @returns {JSX.Element}
 */
const SimpleAutoAssignAlgorithm = (props) => {

  const {
    Districts,
    Users,
    Cases,
    Languages
  } = useSelector(state => state.user || {})

  const { usersInShift, shift, filter, autoAssigning, setAutoAssigning, onAssignmentSuccess } = props;

  // need to refresh the allocateDetails when the shift changes
  useEffect(() => {
    setAllocateDetails(null);
  }, [shift]);

  // State for Popover
  const filteredFilter = useMemo(() => {
    return remoteNullValueFromObject(filter);
  }, [filter]);

  /**
   * In the filtered filter, we can have properties that could have values like this:
   * value: [],
   * value: 'string',
   * value: 'number',
   * 
 * So we need to create diffrent states for the array keys and the single value keys
 */
  // const [arrayKeys, setArrayKeys] = useState(new Map()); // Removed duplicate declaration

  const usersAbsent = useMemo(() => {
    return usersInShift.filter(user => user.attendance?.status !== 'present');
  }, [usersInShift]);

  const shiftTotalLimit = useMemo(() => {
    if (!shift || !usersInShift) {
      return 0;
    }

    const perUserLimit = shift.number;

    let usersInShiftLength = usersInShift.length;

    // Need to remove from length where the user attendance status is not present
    // usersInShift.forEach(user => {
    //   if (user.attendance?.status !== 'present') {
    //     usersInShiftLength--;
    //   }
    // });

    const totalUsers = usersInShiftLength;

    // console.log('totalUsers', totalUsers)

    return perUserLimit * totalUsers;

  }, [shift, usersInShift])

  const totalAllocated = useMemo(() => {
    if (!shift || !usersInShift) {
      return 0;
    }

    let allocatedCount = 0;
    for (const user of usersInShift) {
      allocatedCount += user.allocated;
    }

    return allocatedCount;

  }, [shift, usersInShift])

  const hasFilters = filteredFilter && Object.keys(filteredFilter).length > 0; // Use Object.keys for consistency
  const isOverLimit = totalAllocated >= shiftTotalLimit;

  // Filter key separation logic (still needed here to pass down to ShiftFilterDisplay)
  const [arrayKeys, setArrayKeys] = useState(new Map());
  const [singleValueKeys, setSingleValueKeys] = useState(new Map());

  const [allocatedDetailsLoading, setAllocatedDetailsLoading] = useState(false);
  const [allocateDetails, setAllocateDetails] = useState(null);
  const [viewMode, setViewMode] = useState('preview'); // State for toggle: 'preview' or 'agents'

  useEffect(() => {
    const newArrayKeys = new Map();
    const newSingleValueKeys = new Map();

    Object.keys(filteredFilter).forEach(key => {
      if (Array.isArray(filteredFilter[key])) {
        newArrayKeys.set(key, true);
      } else {
        newSingleValueKeys.set(key, true);
      }
    });

    setArrayKeys(newArrayKeys);
    setSingleValueKeys(newSingleValueKeys);
  }, [filteredFilter]);

  // Function to fetch cases based on the current filter
  const handleFetchCases = async () => {
    console.log('Fetching cases with filter:', filter);
    setAllocatedDetailsLoading(true); // Set loading true
    setAllocateDetails(null); // Clear previous details
    try {
      const response = await axios.post(`${API_HOST_CRM}/cases/auto-assign/filtered-details`, {
        filter: removeNullValueFromObject(filter),
        shift: shift._id,
        users: usersInShift.map(user => user._id),
      });
      console.log('Fetch Cases Response:', response.data);

      setAllocateDetails(response.data.allocateDetails);
    } catch (error) {
      console.error('Error fetching cases:', error);
      // TODO: Handle error (e.g., show notification)
    } finally {
      setAllocatedDetailsLoading(false); // Set loading false
    }
  };

  const handleViewChange = (event, newViewMode) => {
    if (newViewMode !== null) { // Prevent unselecting all buttons
      setViewMode(newViewMode);
    }
  };

  // Function to assign the cases based on the preview
  const assignCases = async () => {
    if (!allocateDetails || Object.keys(allocateDetails).length === 0) {
      toast.error('No allocation details available to assign.');
      return;
    }

    if (allocateDetails.languageBasedCount === 0) {
      toast.error('No cases to assign.');
      return;
    }

    setAutoAssigning(true); // Use the prop function
    const loadingToastId = toast.loading('Assigning cases...');

    try {
      /**
       * allocateDetails will be in the shape of 
       * {
       * userAllocatedCountMap: {
       * userId: {
       * newCases: [CaseData]
       * }
       * }
       * }
       */
      // Transform allocateDetails into agentCases format
      const userAllocatedCountMap = allocateDetails.userAllocatedCountMap;

      const agentCases = Array.from(Object.values(userAllocatedCountMap)).map(allocateData => {
        if (allocateData.newCasesCount === 0) {
          return null;
        }

        let newCasesMapped = allocateData.newCases.map(caseData => {
          return {
            _id: caseData._id,
            // language: caseData.language
          }
        })

        return {
          agentId: allocateData.user._id,
          cases: newCasesMapped
        }
      }).filter(Boolean);

      console.log('Assigning cases with payload:', agentCases);

      const response = await axios.post(`${API_HOST_CRM}/cases/auto-assign/assign-cases`, {
        agentCases: agentCases,
        // shiftId: shift._id // Include shiftId if needed by the backend
      });

      console.log('Assign Cases Response:', response.data);
      toast.success('Cases assigned successfully!', { id: loadingToastId });
      setAllocateDetails(null); // Clear details after successful assignment
      if (onAssignmentSuccess) {
        onAssignmentSuccess(); // Call the callback to refresh parent data
      }

    } catch (error) {
      console.error('Error assigning cases:', error);
      const errorMessage = error.response?.data?.message || 'Failed to assign cases.';
      toast.error(errorMessage, { id: loadingToastId });
    } finally {
      setAutoAssigning(false); // Use the prop function
    }
  };


  return (
    <Stack spacing={2}>
      {/* Header */}
      <Box display="flex" alignItems="center">
        <Typography variant="h6" sx={{ mr: 1 }}>
          Simple Auto Assign Algorithm
        </Typography>
        <Tooltip title="Assigns filtered cases based on selected shift capacity.">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Render the extracted component */}
      <ShiftFilterDisplay
        usersInShift={usersInShift}
        usersAbsent={usersAbsent}
        shiftTotalLimit={shiftTotalLimit}
        totalAllocated={totalAllocated}
        isOverLimit={isOverLimit}
        hasFilters={hasFilters}
        filteredFilter={filteredFilter}
        arrayKeys={arrayKeys}
        singleValueKeys={singleValueKeys}
        filter={filter}
        shift={shift}
        handleFetchCases={handleFetchCases}
      />

      {/* Loading Indicator */}
      {allocatedDetailsLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 2, mt: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Fetching allocation details...</Typography>
        </Box>
      )}

      {/* Toggle Buttons and Conditional Content */}
      {!allocatedDetailsLoading && allocateDetails && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {/* Make ToggleButtonGroup container stretch */}
          <Box display="flex" justifyContent="stretch" sx={{ width: '100%' }}>
            <ToggleButtonGroup
              color="primary"
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              aria-label="View mode"
              size="small"
              // Make ToggleButtonGroup take full width and allow wrapping if needed
              sx={{ width: '100%', '& .MuiToggleButtonGroup-grouped': { flexGrow: 1 } }}
            >
              <ToggleButton value="preview">Preview</ToggleButton>
              <ToggleButton value="agents-preview">Agent Preview</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === 'preview' && (
            <AllocationPreviewDisplay allocateDetails={allocateDetails} shift={shift} shiftTotalLimit={shiftTotalLimit} />
          )}

          {viewMode === 'agents-preview' && (
            <AgentPreviewDisplay allocateDetails={allocateDetails} shift={shift} />
          )}
        </Stack>
      )}

      {/* Assign Button */}
      {!allocatedDetailsLoading && allocateDetails && (
        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 2 }}> {/* Stretch on xs, flex-end on sm+ */}
          <Button
            variant="contained"
            color="primary"
            onClick={assignCases}
            disabled={autoAssigning || !allocateDetails || Object.keys(allocateDetails).length === 0} // Disable during assigning or if no details
            sx={{ width: { xs: '100%', sm: 'auto' } }} // Full width on xs, auto on sm+
          >
            {autoAssigning ? <CircularProgress size={24} color="inherit" /> : 'Assign Cases'}
          </Button>
        </Box>
      )}

    </Stack>

  );
};

export default SimpleAutoAssignAlgorithm;
